import { create } from "zustand";
import { EMPTY_LIST_FILTERS, type ListSortKey, type ManualListFilters } from "./manual-list-model.js";

/**
 * The list pages' CLIENT state (sort, direction, grouping, the filters, the archived switch), per page, held ABOVE
 * the workspace (Zustand — client state per ADR-001): opening a row's details drawer unmounts the workspace, and the
 * page must come back with the same list when the user presses Back (plan panel #2). Reset on reload, as the
 * legacy's `filterOptions` were. The Overview's search box and doughnut segments PRESET the Manuals page through it
 * (ADR-034 D5, folded: no URL params — a preset is transient, exactly the legacy's `filterOptions`).
 */
export type ListPage = "manuals" | "favourites" | "ratings";

export interface ListPageState {
  sort: ListSortKey;
  reverse: boolean;
  grouped: boolean;
  filters: ManualListFilters;
  /** The legacy's dashboard-only "show archived" switch (the Manuals page). */
  archived: boolean;
}
const DEFAULT_PAGE: ListPageState = { sort: "name", reverse: false, grouped: true, filters: EMPTY_LIST_FILTERS, archived: false };
const DEFAULTS: Record<ListPage, ListPageState> = {
  manuals: DEFAULT_PAGE,
  favourites: { ...DEFAULT_PAGE, sort: "favourited" }, // the legacy favourites came favourited-at DESC
  ratings: DEFAULT_PAGE,
};

interface DashboardListStore {
  pages: Record<ListPage, ListPageState>;
  patch: (page: ListPage, patch: Partial<ListPageState>) => void;
  setFilters: (page: ListPage, patch: Partial<ManualListFilters>) => void;
  /** Clear the filters (the legacy "Filter zurücksetzen") — sort / grouping stay. */
  resetFilters: (page: ListPage) => void;
  /** An Overview preset: the page's DEFAULT state + exactly these filters (a fresh start, as the legacy's `filterOptions`). */
  preset: (page: ListPage, filters: Partial<ManualListFilters>) => void;
}

export const useDashboardListStore = create<DashboardListStore>((set) => ({
  pages: DEFAULTS,
  patch: (page, patch) => set((s) => ({ pages: { ...s.pages, [page]: { ...s.pages[page], ...patch } } })),
  setFilters: (page, patch) => set((s) => ({ pages: { ...s.pages, [page]: { ...s.pages[page], filters: { ...s.pages[page].filters, ...patch } } } })),
  resetFilters: (page) => set((s) => ({ pages: { ...s.pages, [page]: { ...s.pages[page], filters: EMPTY_LIST_FILTERS } } })),
  preset: (page, filters) => set((s) => ({ pages: { ...s.pages, [page]: { ...DEFAULTS[page], filters: { ...EMPTY_LIST_FILTERS, ...filters } } } })),
}));

/** Test helper: back to the defaults (module-level state would otherwise leak between tests). */
export function resetDashboardListStore(): void {
  useDashboardListStore.setState({ pages: DEFAULTS });
}
